import React from "react";
import { useObserver, useLocalStore } from "mobx-react";
import { makeStyles } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { ThemeType } from "@theme";
import { SpinnerOverlay } from "@components";
import Body from "@components/typography/Body";
import { ReminderDialog } from "@containers/ReminderDialog/ReminderDialog";
import { CommentBoxData, CommentBoxStore } from "./CommentBoxStore";
import { Form } from "@components/formx";
import UserAvatar from "@containers/UserAvatar";
import CommentTextField from "./CommentTextField";
import { ReminderFormData } from "@containers/ReminderDialog/ReminderStore";
import { LeftControls } from "./LeftControls";
import { RightControl } from "./RightControl";
import { useNavigation } from "@navigation";

export type ButtonType =
  | "comment"
  | "reminder"
  | "metInPerson"
  | "SMS"
  | "mail"
  | "chat"
  | "interest";

export type Props<T> = {
  disabled?: boolean;
  disabledText?: string;
  extraContentBefore?: React.ReactNode;
  smsDisabled?: boolean;
  chatDisabled?: boolean;
  uploadDisabled?: boolean;
  userName?: string;
  withButtons: ButtonType[];
  onFilter?: (eventType: T[]) => void;
  filterOptions?: T[];
  selectedFilter?: T[];
  withNoAnswer?: boolean;
  withUpload?: boolean;
  teamsDisabled?: boolean;
  onComment?: (
    data: CommentBoxData,
    reminder?: ReminderFormData,
    store?: CommentBoxStore
  ) => Promise<any>;
  onNoAnswer?: (data: CommentBoxData) => Promise<any>;
  onMetInPerson?: (data: CommentBoxData, reminder?: ReminderFormData) => Promise<any>;
  onReminder?: (data: CommentBoxData) => Promise<any>;
  onSMS?: (data: CommentBoxData) => Promise<any>;
  onChat?: (data: CommentBoxData) => Promise<any>;
  onEmail?: () => Promise<any>;
  onUpload?: () => any;
  onInterest?: (data: CommentBoxData) => Promise<any>;
  skipReminder?: boolean;
};

export enum DropdownBtnOptions {
  Comment = 0,
  Reminder = 1,
  SMS = 2,
  Email = 3
}

export function CommentBox<T>(props: Props<T>) {
  const store = useLocalStore(() => new CommentBoxStore(props));
  const classes = useStyles({});
  const { disabled, extraContentBefore, userName, teamsDisabled = true } = props;
  const { dirty, isSubmitting } = store;

  const avatarProps = userName ? { name: userName } : null;
  const nav = useNavigation();

  return useObserver(() => {
    nav.lockNavigation("comment-box", dirty);
    return (
      <Form allowUpdates store={store}>
        <ReminderDialog
          onClose={store.cancelReminderDialog}
          open={store.isReminderDialogOpen}
          initialValues={store.reminderDialogInitialValues}
          onSkip={store.skipReminderDialog}
          onSubmit={store.submitReminderDialog}
          teamsDisabled={teamsDisabled}
        />

        {extraContentBefore}

        {props.disabled && props.disabledText && (
          <div className={classes.disabled}>
            <Body>{props.disabledText}</Body>
          </div>
        )}

        <div className={classes.root} data-test="CommentBox">
          <SpinnerOverlay show={isSubmitting} transparent />

          <div className={classes.commentSection}>
            {avatarProps && (
              <Grid item>
                <UserAvatar currentUser size="small" />
              </Grid>
            )}

            <CommentTextField
              disabled={isSubmitting || disabled}
              onChange={(e) => store.setValue("text", e.target.value)}
              name="text"
              value={store.values.text}
            />
          </div>

          <div className={classes.extraActions}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width:
                  store.dropdownBtnIndex === DropdownBtnOptions.Reminder ? "100%" : ""
              }}
            >
              <LeftControls store={store} commentProps={props} />
            </div>
            <div>
              <RightControl store={store} commentProps={props} />
            </div>
          </div>
        </div>
      </Form>
    );
  });
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    ...theme.mixins.childrenMarginBottom(theme.spacing()),
    position: "relative",
    marginBottom: 16
  },

  commentSection: {
    display: "flex",
    flex: "1 0 0",
    marginRight: -1 * theme.spacing(),

    "& > *": {
      marginRight: theme.spacing()
    }
  },

  extraActions: {
    display: "flex",
    flex: "1 0 0",
    justifyContent: "space-between"
  },

  reminderDialog: {
    minWidth: 400
  },

  disabled: {
    textAlign: "center",
    fontStyle: "italic"
  }
}));

export default CommentBox;
